import React, { useEffect, useState } from 'react';

import { useIQGame } from './hooks/useIQGame';
import { calcIQ, iqTier } from './utils/iqUtils';
import ScoreHistoryScreen from "./utils/ScoreHistoryScreen";
import IQLeaderboardScreen from "./utils/IQLeaderboardScreen";
import { usePDActiveSubscription } from '../../purpledoor/hooks/pdActiveSubscription';
import Gumball from "../gumball/Gumball";
import GumballHistoryPage from "../components/GumballHistoryPage";

export default function IQTesterHome() {
  const game = useIQGame();
  const {
      subscriptions,
      selectedSubscription,
      loadingSubs,
      product,
      isActive,
      daysLeft,
      formatRange,
      refetch
  } = usePDActiveSubscription();

  if (loadingSubs) return <p>Loading subscription...</p>;

  return (
    <div className="mt-4 min-h-screen bg-[#0F0A1E] text-white font-sans relative overflow-hidden dark border-y-2 border-pink-400 rounded-xl">
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-[-60px] right-[-60px] w-[200px] h-[200px] bg-violet-600 rounded-full blur-[60px] opacity-20" />
        <div className="absolute bottom-[120px] left-[-60px] w-[160px] h-[160px] bg-pink-500 rounded-full blur-[60px] opacity-20" />
        <div className="absolute top-[40%] right-[-40px] w-[120px] h-[120px] bg-cyan-500 rounded-full blur-[60px] opacity-20" />
      </div>

      {game.screen === 'home' && <HomeScreen game={game} isActive={isActive} daysLeft={daysLeft} />}
      {game.screen === 'game' && <GameScreen game={game} />}
      {game.screen === 'streakConfirm' && <StreakConfirmScreen game={game} />}
      {game.screen === 'results' && <ResultsScreen game={game} />}
      {game.screen === 'leaderboard' && <IQLeaderboardScreen game={game} />}
      {game.screen === 'scorehistory' && <ScoreHistoryScreen game={game} />}
      {game.screen === 'gumball' && <GumballScreen game={game} />}
      {game.screen === 'rewardhistory' && (
        <GumballHistoryPage onClose={() => game.setScreen('home')} />
      )}
    </div>
  );
}

/* ====================== GUMBALL ====================== */
function GumballScreen({ game }) {
  // Gumball expects history rows shaped as { totalScore }. IQTester's log
  // rows track xp instead — mapped here so Gumball.jsx stays generic and
  // untouched.
  const gumballHistory = (game.streakHistory || []).map((row) => ({
    totalScore: row.xp || 0,
  }));

  return (
    <Gumball
      gameKey="iqtester"
      history={gumballHistory}
      streakInfo={{ currentStreak: game.streak }}
      onClose={() => { game.stopGame(); game.setScreen('home'); }}
    />
  );
}

/* ====================== HOME ====================== */
function HomeScreen({ game, isActive, daysLeft }) {
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [pickerMode, setPickerMode] = useState("home");

  const stats = game.myStats;
  const bestIQ = stats?.bestScore?.iqScore || null;
  const bestScore = stats?.bestScore || 0;
  const [showStreakModal, setShowStreakModal] = useState(false);

  const [selectedLevel, setSelectedLevel] = useState(game.currentLevel);
  const [selectedSet, setSelectedSet] = useState(game.currentSet);

  useEffect(() => {
    setSelectedLevel(game.currentLevel);
    setSelectedSet(game.currentSet);
  }, [game.currentLevel, game.currentSet]);

  const levels = [];
  for (let i = game.currentLevel + 1; i >= 1; i--) {
      if (i <= 10)
          levels.push(i);
  }

  const maxSet = selectedLevel === game.currentLevel
      ? Math.min(game.currentSet + 1, 3)
      : 3;

  const sets = [];
  for (let i = maxSet; i >= 1; i--) {
      sets.push(i);
  }

  return (
    <div className="mt-4 w-full flex flex-col items-center justify-center px-2 py-2 gap-2 relative z-10">
      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-pink-500 text-white text-xs font-bold tracking-[2px] uppercase px-5 py-2.5 rounded-2xl">
        <div className="w-2 h-2 bg-white rounded-full" />
          Think5Whys IQAddiction
        </div>

        <h1 className="text-xl lg:text-2xl font-bold text-center leading-none tracking-tighter text-cyan-200">
          How big is
          <span className="ml-2 bg-gradient-to-r from-violet-300 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
            your brain?
          </span>
        </h1>

        <p className="text-center text-zinc-200 text-[11px] lf:text-sm leading-relaxed max-w-[280px]">
          10 levels. No cap.<br />Flex your IQ on the timeline 🧠
        </p>

        <div className="w-full bg-[#221540] border border-violet-500/30 rounded-2xl p-2 flex items-center gap-2">
          <div className="flex-1 text-center">
            <button
              onClick={() => { game.stopGame(); game.setScreen("scorehistory")}}
              className="w-fit px-2 py-1 rounded-md text-lg lg:text-xl font-bold text-yellow-400 font-mono"
            >
              IQTester Best Score History - {game?.streakHistory?.length || 0}
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-col items-center">
          {isActive ? (
              <div className="text-center bg-green-900/30 border border-green-500/50 rounded-2xl px-2 py-1 max-w-md">
                <div className="flex items-center justify-center gap-2 text-green-400 mb-1">
                  <span className="text-lg lg:text-xl">🎉</span>
                  <span className="font-semibold text-sm lg:text-lg">
                    Active Subscription -
                    Days Left: <span className="font-bold text-sm lg:text-lg text-white">{daysLeft}</span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center bg-red-900/30 border border-red-500/50 rounded-2xl px-2 py-1 max-w-md">
                <div className="flex items-center justify-center gap-2 text-red-400 mb-1">
                  <span className="text-sm lg:text-lg">⚠️</span>
                  <span className="font-semibold text-sm lg:text-lg">No Active Subscription</span>
                </div>
                <p className="text-red-300 text-sm lg:text-lg mt-1">
                  Your subscription has expired or is inactive
                </p>
              </div>
          )}
        </div>
        <div className='flex flex-row items-center gap-1'>
          <button
            onClick={() => setShowGamePicker(true)}
            className="w-fit px-2 py-2 bg-gradient-to-r from-violet-600 to-pink-500 hover:brightness-110 active:scale-95 transition-all font-bold text-sm lg:text-lg rounded-xl shadow-xl shadow-violet-600/40"
          >
            Start the fun 🚀
          </button>

          <button
            onClick={() => { game.stopGame(); game.setScreen('leaderboard');}}
            className="w-fit px-2 py-2 border border-white/10 hover:border-violet-400 text-zinc-100 font-medium rounded-xl transition-colors"
          >
            See who's smartest 👑
          </button>

          <button
            onClick={() => { game.stopGame(); game.setScreen('gumball');}}
            className="w-fit px-2 py-2 border border-white/10 hover:border-yellow-400 text-zinc-100 font-medium rounded-xl transition-colors"
          >
            🍬 Gumball
          </button>

          <button
            onClick={() => { game.stopGame(); game.setScreen('rewardhistory');}}
            className="w-fit px-2 py-2 border border-white/10 hover:border-emerald-400 text-zinc-100 font-medium rounded-xl transition-colors"
          >
            📜 Reward History
          </button>
        </div>
        {showGamePicker && (
          <div className="mt-3 w-full max-w-md rounded-2xl border border-violet-500/30 bg-[#221540] p-4">
            {pickerMode === "home" && (
              <div className="space-y-3">
                <div className="text-center text-sm font-semibold text-cyan-300">
                  Continue Current Level {game.currentLevel} • Set {game.currentSet}?
                </div>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => {
                      game.startGame(0, 0);
                      setShowGamePicker(false);
                    }}
                    className="w-fit px-3 py-2 rounded-xl bg-cyan-600 font-semibold"
                  >
                    Brain Sprint
                  </button>
                  <button
                    onClick={() => {
                      game.startGame(game.currentLevel, game.currentSet);
                      setShowGamePicker(false);
                    }}
                    className="w-fit px-3 py-2 rounded-xl bg-emerald-600 font-semibold"
                  >
                    Continue Previous
                  </button>
                  <button
                    onClick={() => setPickerMode("level")}
                    className="w-fit px-3 py-2 rounded-xl border border-violet-500"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}

            {pickerMode === "level" && (
              <div>
                <div className="text-center text-cyan-300 mb-3">
                  Select Level
                </div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {levels.map(level => (
                    <button
                      key={level}
                      onClick={() => {
                        setSelectedLevel(level);
                        setPickerMode("set");
                      }}
                      className={`w-fit px-3 py-1 rounded-xl ${
                        selectedLevel === level
                          ? "bg-violet-600"
                          : "bg-[#111827]"
                      }`}
                    >
                      L{level}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {pickerMode === "set" && (
              <div>
                <div className="text-center text-cyan-300 mb-3">
                  Level {selectedLevel} • Select Set
                </div>
                <div className="flex gap-2 justify-center">
                  {sets.map(set => (
                    <button
                      key={set}
                      onClick={() => setSelectedSet(set)}
                      className={`px-3 py-1 rounded-xl ${
                        selectedSet === set
                          ? "bg-pink-600"
                          : "bg-[#111827]"
                      }`}
                    >
                      S{set}
                    </button>
                  ))}
                </div>
                <div className="flex justify-center gap-2 mt-4">
                  <button
                    onClick={() => setPickerMode("level")}
                    className="w-fit px-3 py-2 rounded-xl border border-zinc-500"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => {
                      game.startGame(selectedLevel, selectedSet)
                      setShowGamePicker(false);
                      setPickerMode("home");
                    }}
                    className="w-fit px-3 py-2 rounded-xl bg-violet-600"
                  >
                    Play
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
}

/* ====================== GAME ====================== */
function GameScreen({ game }) {
  const personalBest = game.streakHistory.find(
      s =>
          s.currentLevel === game.currentLevel &&
          s.currentSet === game.currentSet
  );
  const q = game.question;
  if (!q) return null;

  const { timeLeft, timeTotal } = game;
  const pct = timeLeft / timeTotal;
  const CIRC = 138.2;
  const ringColor = pct > 0.4 ? '#06B6D4' : pct > 0.2 ? '#FBBF24' : '#EF4444';

  return (
    <div className="min-h-screen flex flex-col relative z-10">
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <div className="bg-violet-500/10 border border-violet-500/30 text-violet-300 text-xs font-bold px-3 py-1.5 rounded-xl">
          <div>
            {game.currentLevel === 0 && game.currentSet === 0
              ? "Warm-Up -  Level 0 • Set 0"
              : `Level ${game.currentLevel} • Set ${game.currentSet}`}
          </div>

          {personalBest && (
            <div className="text-[10px] text-yellow-300 mt-1">
              🏆 IQ {personalBest.iqAtTime} • ⚡ {(personalBest.timeTaken / 1000).toFixed(1)}s
            </div>
          )}
        </div>

        <div className="relative w-14 h-14">
          <svg width="56" height="56" viewBox="0 0 52 52" className="-rotate-90">
            <circle cx="26" cy="26" r="22" fill="none" stroke="#ffffff10" strokeWidth="4" />
            <circle
              cx="26" cy="26" r="22" fill="none"
              stroke={ringColor}
              strokeWidth="4"
              strokeDasharray={CIRC}
              strokeDashoffset={CIRC * (1 - pct)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-mono text-lg font-bold" style={{ color: ringColor }}>
            {timeLeft}
          </div>
        </div>

        <div className="bg-yellow-400/10 border border-yellow-400/30 text-yellow-400 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
          🔥 {game.streak}
        </div>
      </div>

      {/* Progress */}
      <div className="px-5 flex gap-1.5 pb-4">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className={`flex-1 h-1.5 rounded-full transition-colors ${
              i < game.currentQ ? 'bg-emerald-500' : i === game.currentQ ? 'bg-violet-400' : 'bg-white/10'
            }`}
          />
        ))}
      </div>

      {/* Question */}
      <div className="px-5 flex flex-col">
        <div className="uppercase tracking-widest text-cyan-400 text-xs font-bold mb-2">
          {q.category}
        </div>
        <div className="text-lg lg:text-xl font-medium leading-tight mb-4">{q.text}</div>
        {q.visual && (
          <div className="mt-2 bg-[#221540] border border-violet-500/20 rounded-xl px-2 py-1 text-center font-mono text-xl lg:text-xl tracking-widest text-violet-300">
            {q.visual}
          </div>
        )}
      </div>

      {/* Options */}
      <div className="grid grid-cols-2 gap-3 px-4 pb-3 mt-8">
        {q.options.map((opt) => {
          let classes = "min-h-[34px] flex items-center justify-center text-center px-4 py-2 rounded-2xl border font-semibold transition-all active:scale-95";
          
          if (game.answered) {
            if (opt === q.answer) classes += " bg-emerald-900/30 border-emerald-500 text-emerald-300";
            else if (opt === game.selectedOpt) classes += " bg-red-900/30 border-red-500 text-red-300";
            else classes += " bg-[#221540] border-white/10";
          } else {
            classes += " bg-[#221540] border-white/10 hover:border-violet-400";
          }

          return (
            <button key={opt} className={classes} onClick={() => game.selectAnswer(opt)}>
              {opt}
            </button>
          );
        })}
      </div>

      {/* Feedback Overlay */}
      {game.feedback && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 px-6">
          <div className="text-center">
            <div className="text-2xl lg:text-4xl mb-4">{game.feedback.correct ? '🧠' : '😬'}</div>
            <div className={`text-lg lg:text-xl font-bold mb-3 ${game.feedback.correct ? 'text-emerald-400' : 'text-red-400'}`}>
              {game.feedback.title}
            </div>
            <div className="text-zinc-200 max-w-[260px] mx-auto leading-relaxed">
              {game.feedback.sub}
            </div>
            {game.feedback.xpEarned > 0 && (
              <div className="mt-2 text-xl lg:text-2xl font-bold text-yellow-400 font-mono">
                +{game.feedback.xpEarned} XP
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== STREAK CONFIRM ====================== */
function StreakConfirmScreen({ game }) {
  const d = game.streakConfirmData;
  if (!d) return null;
    const bonus = game.streakHistory.length;
    const previous = game.streakHistory.find(
    s =>
      s.currentLevel === game.currentLevel &&
      s.currentSet === game.currentSet
  );

  const canSave = d.correct > 0 &&
    (
      !previous ||
      d.xp > (previous.xp || 0)
    );
  // const bestScore = game.bestScore?.iqScore || 0;

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-4 bg-[#0F0A1E] relative z-10">
      <div className="bg-[#221540] border border-yellow-400/30 rounded-xl px-r py-2 w-full max-w-md text-center">
        <div className="text-2xl lg:text-4xl mb-4">🔥</div>
        <h2 className="text-xl lg:text-2xl font-bold text-yellow-400 mb-4">{d.streak}-day streak!</h2>
        
        <p className="text-zinc-200 leading-relaxed">
          {canSave ? (
            <>
              🎉 New personal best for this Level & Set!<br />
              Save your score and earn <span className="text-yellow-400 font-bold">+{bonus} bonus XP</span>.
            </>
          ) : (
            <>
              You need to beat your previous XP for this Level & Set to save a new record.
            </>
          )}
        </p>

        <div className="flex justify-center gap-12 my-8">
          <div className="text-center">
            <div className="text-xl lg:text-2xl font-bold text-violet-300 font-mono">{d.iq}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-200 mt-1">IQ Score</div>
          </div>
          <div className="text-center">
            <div className="text-xl lg:text-2xl font-bold text-emerald-400">{d.correct}/10</div>
            <div className="text-xs uppercase tracking-widest text-zinc-200 mt-1">Correct</div>
          </div>
        </div>

        <button
          // onClick={() => game.handleStreakConfirm(true)}
          disabled={!canSave}
          onClick={() => {
            if (canSave) game.handleStreakConfirm(true);
          }}
          // disabled={d.correct < 1 || d.iq <= bestScore}
          // className="w-full bg-gradient-to-r from-violet-600 to-pink-500 py-2 rounded-2xl font-bold text-lg mb-3 active:scale-95 transition-all"
          className={`w-full py-2 rounded-2xl font-bold text-lg mb-3 transition-all ${
            canSave
              ? "bg-gradient-to-r from-violet-600 to-pink-500 active:scale-95"
              : "bg-zinc-700 text-zinc-400 cursor-not-allowed"
          }`}
        >
          Yes, save my score 🔥
        </button>
        <button
          onClick={() => game.handleStreakConfirm(false)}
          className="w-full border border-white/10 py-2 rounded-2xl text-zinc-200 hover:text-white transition-colors"
        >
          Skip, show results
        </button>
      </div>
    </div>
  );
}

/* ====================== RESULTS ====================== */
function ResultsScreen({ game }) {
  const d = game.streakConfirmData;
  if (!d) return null;

  const iq = d.iq;
  const tier = iqTier(iq);
  const apiRes = game.submitResult;
  const previous = game.streakHistory.find(
      s =>
          s.currentLevel === game.currentLevel &&
          s.currentSet === game.currentSet
  );

  const canSave =
      d.correct > 0 &&
      (
          !previous ||
          d.iq > previous.iqAtTime ||
          (
              d.iq === previous.iqAtTime &&
              game.timeTaken < previous.timeTaken
          )
      );
  const isNewBest = apiRes?.isNewBest;

  const handleShare = () => {
    const text = `I just scored IQ ${iq} on PurpleDoor IQ Tester! ${tier.label} 🧠 Can you beat me? #PurpleDoor #IQTest`;
    if (navigator.share) {
      navigator.share({ title: 'My PurpleDoor IQ Score', text });
    } else {
      navigator.clipboard.writeText(text);
      alert('Score copied — paste it anywhere!');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-2 py-2 gap-4 relative z-10">
      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-pink-500 text-white text-xs font-bold tracking-[2px] uppercase px-5 py-2.5 rounded-2xl">
        <div className="w-2 h-2 bg-white rounded-full" /> PurpleDoor
      </div>

      {game.streakMessage && (
        <div className="w-full max-w-md text-center py-1 px-2 bg-[#221540] border border-yellow-400/30 rounded-2xl text-lg font-medium">
          {game.streakMessage}
        </div>
      )}

      {isNewBest && (
        <div className="bg-gradient-to-r from-yellow-400/10 to-yellow-400/5 border border-yellow-400/30 text-yellow-400 px-2 py-1 rounded-xl font-bold text-center w-full">
          🏆 New personal best! {apiRes?.previousBest ? `(was ${apiRes.previousBest})` : 'First game!'}
        </div>
      )}

      <div className="text-center">
        <div className="text-sm uppercase tracking-widest text-zinc-200">Your IQ Score</div>
        <div className="text-8xl font-bold font-mono bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent leading-none my-2">
          {!iq ? 0 : iq}
        </div>
        <div className="text-xl font-bold" style={{ color: tier.color }}>
          {tier.label}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 w-full">
        {[
          { label: "Correct", value: `${d.correct}/10`, color: "text-emerald-400" },
          // { label: "XP Earned", value: d.xp, color: "text-yellow-400" },
          { label: "Avg Speed", value: d.avgSpeed != null ? `${d.avgSpeed}s` : '—', color: "text-cyan-400" },
          // { label: "Streak", value: `${game.streak}d`, color: "text-violet-300" },
        ].map((stat, i) => (
          <div key={i} className="bg-[#221540] border border-white/10 rounded-2xl px-4 py-2 text-center">
            <div className={`text-lg lg:text-xl font-bold font-mono ${stat.color}`}>{stat.value}</div>
            <div className="text-xs uppercase tracking-widest text-zinc-200 mt-2">{stat.label}</div>
          </div>
        ))}
      </div>

      {game.streakSaved && game.submitResult?.remarks && (
        <div className="bg-[#221540] border border-violet-500/20 rounded-2xl px-4 py-2 text-sm italic text-zinc-200">
          💬 {game.submitResult.remarks}
        </div>
      )}
      <div className='flex flex-row gap-0.5'>
        <button 
          onClick={() => { game.startGame(game.currentLevel, game.currentSet); }}
          className="w-fit px-2 py-2 bg-gradient-to-r from-violet-600 to-pink-500 rounded-2xl font-bold text-lg active:scale-95"
        >
          Play Again 🔄
        </button>
        <button onClick={handleShare} className="w-fit px-2 py-2 border border-white/10 rounded-2xl font-medium hover:border-cyan-400 transition-colors">
          Share my score 📤
        </button>
        <button onClick={() => {game.stopGame(); game.setScreen('home');}} className="text-zinc-200 text-sm w-fit px-2 py-2">
          Back to home
        </button>
      </div>
    </div>
  );
}

